import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = window.__SG_API_BASE__;

// Builder 2 — Legal acknowledgment gate.
// After auth succeeds, probe /api/legal/my-acceptance. If the caller has
// not accepted the current versions, redirect to /legal/accept. The
// LegalGate route itself passes `skipLegalGate` so we don't ping-pong.
export const ProtectedRoute = ({ children, skipLegalGate = false }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [probeError, setProbeError] = useState(false);

  const runCheck = () => {
    setProbeError(false);
    setIsChecking(true);
    let cancelled = false;
    axios.get(`${API}/api/legal/my-acceptance`, { withCredentials: true })
      .then(r => {
        if (cancelled) return;
        if (r.data && r.data.up_to_date === false) {
          if (location.pathname !== '/legal/accept') {
            navigate('/legal/accept', { replace: true });
            return;
          }
        }
        setIsChecking(false);
      })
      .catch(() => {
        // v4.3.1 Production P0 — FAIL CLOSED. A failed legal probe must NOT
        // grant access to the protected surface; show a retry instead.
        if (!cancelled) { setProbeError(true); setIsChecking(false); }
      });
    return () => { cancelled = true; };
  };

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    // Re-review P0 — mandatory first-run handle claim: server-issued
    // `needs_handle` gates every protected surface until a handle exists.
    if (!skipLegalGate && user.needs_handle === true && location.pathname !== '/claim-handle') {
      navigate('/claim-handle', { replace: true, state: { from: location.pathname } });
      return;
    }
    if (skipLegalGate) {
      setIsChecking(false);
      return;
    }
    return runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, navigate, location.pathname, skipLegalGate]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F1]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#0D0D0D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#8E8A84] font-light tracking-wide">Loading...</p>
        </div>
      </div>
    );
  }

  if (probeError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F1]" data-testid="legal-probe-error">
        <div className="text-center max-w-sm px-6">
          <p className="text-[#0D0D0D] font-light mb-2">We couldn't verify your account.</p>
          <p className="text-[#8E8A84] text-sm mb-6">This is usually a brief network hiccup.</p>
          <button
            data-testid="legal-probe-retry"
            onClick={runCheck}
            className="px-6 py-2 rounded-full bg-[#0D0D0D] text-white text-sm tracking-wide">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return children;
};
